import React, {useEffect, useMemo, useState} from "react";

import {Button, Checkbox, Col, Dropdown, Menu, Modal, Pagination, Row, Space} from "antd";
import {DownOutlined, EditOutlined, SearchOutlined, SyncOutlined, WarningOutlined} from "@ant-design/icons";
import {ClearFiltersBar} from "@components/issues-filter-drawer";
import {SeriesPaginationQuery, useGetUserSeriesQuery, useRefreshAllUserSeriesFmvMutation} from "@mwServices/Series";
import SeriesGrid from "@components/grid/SeriesGrid";
import MyCollectionSeriesListItem from "@mwPages/my-collection/series/MyCollectionSeriesListItem";
import {PAGINATION_PAGES} from "@components/grid/Grid";
import {BoxesEditDrawer} from "@components/boxes-edit-drawer";
import {useBoxesAddSeriesMutation, useGetBoxesQuery} from "@mwServices/Boxes";
import {showResponse} from "@services/Api";
import {useCollectionRemoveSeriesMutation} from "@mwServices/User";
import AssignConditionModal from "@components/assign-condition-modal";
import AssignLocationModal from "@components/assign-location-modal";
import {SortIcon} from "@components/sort-icon";
import usePreferences from "@hooks/usePreferences";
import SeriesFiltersDrawer from "@mwPages/my-collection/series/SeriesFiltersDrawer";
import {scrollToThen} from "@lib/utils";
import {InfoIcon} from "@components/icons/Icons";
import {DebounceInput} from "@components/debounce-input";
import {showInfoModal} from "@lib/showModal";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {useWindowSize} from "@components/window-size-provider";

const MyCollectionSeries = ({search, setSearch, setAddBox}) => {
    const windowDimensions = useWindowSize();
    const {itemCount, setItemCount} = usePreferences();
    const defaultFilters = useMemo(() => ({
        publishers: undefined,
        formats: undefined,
        query: '',
        sort: 0,
    }), []);
    const [queryParams, setQueryParams] = useState<SeriesPaginationQuery>({
        filters: {...defaultFilters},
        page: 1,
        perPage: itemCount,
    });
    const seriesQuery = useGetUserSeriesQuery(queryParams);
    const pagination = useMemo(() => seriesQuery && seriesQuery.data && seriesQuery.data.pagination ? seriesQuery.data.pagination : null, [seriesQuery]);
    const [editBox, setEditBox] = useState(null);
    const [showEditMultiple, setShowEditMultiple] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const boxesQuery = useGetBoxesQuery({filters: {}});
    const [addSeriesToBox, addSeriesToBoxResponse] = useBoxesAddSeriesMutation();
    const [removeSeriesFromCollection, removeSeriesFromCollectionResponse] = useCollectionRemoveSeriesMutation();
    const [showAssignConditionModal, setShowAssignConditionModal] = useState(false);
    const [showAssignLocationModal, setShowAssignLocationModal] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    const [postRefreshFmv, refreshFmvResponse] = useRefreshAllUserSeriesFmvMutation();

    const subscriptionType = useSubscriptionType();
    const allowBoxes = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    useEffect( () => {
        setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, query: search}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[search]);

    useEffect(() => {
        showResponse(addSeriesToBoxResponse, 'Series added to box.', 'Sorry, there was a problem adding the series to the Box.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addSeriesToBoxResponse]);
    useEffect(() => {
        showResponse(removeSeriesFromCollectionResponse, 'Series removed from collection.', 'Sorry, there was a problem removing the series from the collection.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removeSeriesFromCollectionResponse]);

    useEffect(() => {
        let responses = {success: 0, error: 0};
        if(refreshFmvResponse.isSuccess) {
            if(refreshFmvResponse.data.result){
                responses = refreshFmvResponse.data.result.reduce((accum, item) => {
                    return {
                        success: item.success === 1 ? accum.success+1 : accum.success,
                        error: item.success === 0 ? accum.error+1 : accum.error,
                    }
                } , responses);
            }
        }
        showResponse(refreshFmvResponse,
            ` ${responses.success} Series FMVs updated successfully. ${responses.error} not updated, you must wait 1 hour between updates.`,
            'Sorry, there was a problem refreshing the series FMV.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFmvResponse]);

    useEffect(() => {
        setSelectedIds([]);
    }, [seriesQuery.data]);

    const seriesIds = useMemo(() => {
        if(seriesQuery.data){
            return seriesQuery.data.series.map(item => item.series_id);
        }
        return [];
    }, [seriesQuery]);

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const smallButtons = useMemo(() => {
        return windowDimensions.under(1200);
    }, [windowDimensions]);

    const isLoading = useMemo(() => {
        return seriesQuery.isLoading || seriesQuery.isFetching ||
            refreshFmvResponse.isLoading || addSeriesToBoxResponse.isLoading
    }, [seriesQuery, refreshFmvResponse, addSeriesToBoxResponse]);

    const toggleSelected = (id: number) => {
        setSelectedIds(currentIds => {
            if(currentIds.indexOf(id) === -1) {
                return [...currentIds, id];
            } else {
                return currentIds.filter((item) => item !== id);
            }
        })
    };

    return (
        <>
            <div className="mar-5">
                <Row className="flex-space-between">
                    <Col>
                        <Space>
                            <Button className={"greyButton " + (showEditMultiple ? ' selectedBlue' : '')} size={"large"}  onClick={() => setShowEditMultiple(!showEditMultiple)} >
                                <span className="filterButtonText">Edit</span>
                                <EditOutlined className="font-12"  />
                            </Button>
                        </Space>
                    </Col>
                    <Col>
                        <Space>

                            {!windowDimensions.isLargeScreen && (
                                <Button className="greyButton " size="large" onClick={() => setShowSearch(!showSearch)}>
                                    <SearchOutlined />
                                </Button>
                            )}

                            <SeriesFiltersDrawer
                                filters={queryParams.filters}
                                setFilters={(filters) => setQueryParams({...queryParams, page: 1, filters})}
                            />

                            {pagination && (
                                <Pagination
                                    current={pagination.current_page}
                                    pageSize={pagination.per_page}
                                    total={pagination.total_items}
                                    onChange={(page) => {
                                        if(queryParams && queryParams.page !== page){
                                            setQueryParams({...queryParams, page: page});
                                        }
                                    }}
                                    simple
                                />
                            )}
                        </Space>
                    </Col>
                </Row>

                {showEditMultiple && (
                    <Row className={'edit-multiple-row ' + (windowDimensions.isLargeScreen ? 'large-screen ' : '')}>
                        <Col span={24} className="edit-multiple-col">
                            <Checkbox
                                checked={seriesQuery.data && selectedIds.length === seriesQuery.data.series.length}
                                onChange={() => {
                                    if(seriesQuery.data){
                                        if(selectedIds.length === seriesQuery.data.series.length){
                                            setSelectedIds([]);
                                        }else{
                                            setSelectedIds(seriesQuery.data.series.map(series => series.series_id) as number[]);
                                        }
                                    }
                                }}
                            />

                            {allowBoxes && (
                                <Dropdown
                                    trigger={["click"]}
                                    className="greyButton selectButton"
                                    disabled={selectedIds.length === 0}
                                    overlay={(
                                        <Menu >
                                            {boxesQuery.data && boxesQuery.data.boxes.map((box) => (
                                                <Menu.Item key={box.id} onClick={() => addSeriesToBox({box_id: box.id, series_ids: selectedIds})}>{box.name}</Menu.Item>
                                            ))}
                                            <Menu.Item key={0} onClick={() => { if(setAddBox) setAddBox({show: true, series_ids: selectedIds, issue_ids: []})}}>+ Create new Box</Menu.Item>
                                        </Menu>
                                    )}
                                >
                                    <Button
                                        className="greyButton mar-top-5 edit-multi-item"
                                        size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                    >
                                        Add to Virtual Box
                                        <DownOutlined />
                                    </Button>
                                </Dropdown>
                            )}

                            <Button
                                className="greyButton mar-top-5 edit-multi-item"
                                size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                disabled={selectedIds.length === 0}
                                onClick={() => { setShowAssignLocationModal(true)}}
                            >
                                Add Location
                            </Button>

                            <Button
                                className="greyButton mar-top-5 edit-multi-item"
                                size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                disabled={selectedIds.length === 0}
                                onClick={() => Modal.confirm({
                                    autoFocusButton: null,
                                    icon: <WarningOutlined />,
                                    content: (<div>
                                        <div><b>Delete Series</b></div>
                                        <div className="pad-top-10">
                                            Are you sure?<br/>
                                            Selecting this option will delete these series permanently. This action cannot be undone.
                                        </div>
                                    </div>),
                                    onOk() {
                                        removeSeriesFromCollection({series_ids: selectedIds});
                                    },
                                })}
                            >
                                Delete
                            </Button>

                            <Button
                                className="greyButton mar-top-5 edit-multi-item"
                                size={windowDimensions.isLargeScreen ? "large" : 'middle'}
                                disabled={selectedIds.length === 0}
                                onClick={() => setShowAssignConditionModal(true)}
                            >
                                Assign Grade
                            </Button>
                        </Col>

                        <AssignConditionModal
                            isModalOpen={showAssignConditionModal}
                            onCancel={() => setShowAssignConditionModal(false)}
                            selectedSeriesIds={selectedIds}
                        />
                        
                        <AssignLocationModal
                            isModalOpen={showAssignLocationModal}
                            onCancel={() => setShowAssignLocationModal(false)}
                            selectedSeriesIds={selectedIds}
                        />
                    </Row>

                )}

                {showSearch && (
                    <Row className="my-collection-issues-container top-line" >
                        <DebounceInput
                            placeholder={"Search Series..."}
                            className="searchInput"
                            prefix={<SearchOutlined />}
                            value={search}
                            maxLength={100}
                            debounceTimeout={2000}
                            onChange={(event) => {
                                const { value } = event.target;
                                if (setSearch && (value !== search)) setSearch(value);
                            }}
                        />
                    </Row>
                )}

                <ClearFiltersBar
                    filters={queryParams.filters}
                    defaultFilters={defaultFilters}
                    issueCount={pagination && pagination.total_items}
                    onClear={() => {
                        setSearch('');
                        setQueryParams({...queryParams, page: 1, filters: defaultFilters});
                    }}
                />

                {showDesktopView && (
                    <Row className="list-view-header">
                        {showEditMultiple && (
                            <Col span={1}>
                            </Col>
                        )}
                        <Col className={smallButtons ? "width-85px" : "width-135px"}>
                        </Col>
                        <Col className="pad-left-20 flex-grow-1">
                            <div className="flex">
                                Title
                                <SortIcon
                                    sort={queryParams.filters.sort}
                                    sortAsc={0}
                                    sortDesc={1}
                                    onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                />
                            </div>
                        </Col>
                        <Col span={3} className="no-wrap">
                            <div className="flex-align-center">
                                # of Comics
                                <SortIcon
                                    sort={queryParams.filters.sort}
                                    sortAsc={2}
                                    sortDesc={3}
                                    onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                />
                            </div>
                        </Col>
                        <Col span={4} className="no-wrap">
                            <div className="flex-align-center">
                                Total FMV
                                {showPriceInfo && (
                                    <>
                                        <SortIcon
                                            sort={queryParams.filters.sort}
                                            sortAsc={4}
                                            sortDesc={5}
                                            onChange={(sort) => setQueryParams({...queryParams, page: 1, filters: {...queryParams.filters, sort}})}
                                        />
                                        <Button
                                            shape={"circle"}
                                            size={"small"}
                                            className={"greyButton mar-left-5"}
                                            icon={<SyncOutlined className={'fmv-update '} />}
                                            onClick={
                                                () => {
                                                    showInfoModal({
                                                        title: "Update All Series Values",
                                                        content: (<>
                                                            <p>
                                                                Selecting this option allows you to update all of your Series Values on this page at once,
                                                                however it may take over 30 seconds to process.
                                                            </p>
                                                            <p>Do you wish to proceed?</p>
                                                        </>),
                                                        onOk:  async () => {
                                                            await postRefreshFmv({series_ids: seriesIds});
                                                        },
                                                        okCancel: true,
                                                    });
                                                }}
                                        />
                                    </>
                                )}
                                <Button
                                    className="info-icon"
                                    type="link"
                                    icon={<InfoIcon className={`gain-loss-icon`} />}
                                    onClick={
                                        () => {
                                            showInfoModal({
                                                title: "Total FMV",
                                                content: (<>
                                                    <p>
                                                        The Total FMV column allows you to know what each series in your&nbsp;
                                                        <b>COVR</b>PRICE collection are worth, based on the individual FMVs for each comic
                                                        you've added to that series.
                                                    </p>
                                                    <p>
                                                        You can either update each series individually (by selecting the refresh
                                                        button next to each series FMV) or all at once (by selecting the refresh
                                                        icon in the column header).
                                                    </p>
                                                </>),
                                            });
                                        }}
                                />
                            </div>
                        </Col>
                        <Col span={3} className="no-wrap">
                            <div className="flex-align-center">Series Type</div>
                        </Col>
                        <Col span={3} className="no-wrap">
                            <div className="flex-align-center">Actions</div>
                        </Col>
                    </Row>
                )}

                <SeriesGrid
                    isLoading={isLoading}
                    data={seriesQuery.data}
                    noDataMessage="Series Info Will Be Added Once Individual Comics Are Added To Your Collection"
                    SeriesRender={(props) => (<MyCollectionSeriesListItem
                        {...props}
                        toggleSelected={toggleSelected}
                    />)}
                    getItemProps={(item) => ({
                        key: item.series_id,
                        showEditMultiple,
                        showDesktopView,
                        isSelected: selectedIds.indexOf(item.series_id) !== -1,
                    })}
                    paginationType={PAGINATION_PAGES}
                    onPaginationChange={(page => {
                        if(queryParams && queryParams.page !== page){
                            scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                setQueryParams({ ...queryParams, page: page });
                            });
                        }
                    })}
                    onPaginationSizeChange={(current: number, perPage: number) => {
                        if(queryParams && queryParams.perPage !== perPage){
                            scrollToThen({top: 0, behavior: 'smooth'}, () => {
                                setQueryParams({...queryParams,
                                    page: 1,
                                    perPage: perPage,
                                });
                                setItemCount(perPage);
                            });
                        }
                    }}
                />
            </div>

            <BoxesEditDrawer visible={editBox !== null} box={editBox} onClose={() => {setEditBox(null)}} />
        </>
    );
};

export default MyCollectionSeries;